import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ArrowLeft, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../api/axios';

const ReviewsDashboard = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [loading, setLoading] = useState(true);
  const [filterRating, setFilterRating] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 20;

  const fetchReviews = useCallback(async () => {
    try {
      const params = { limit: PER_PAGE, skip: page * PER_PAGE };
      if (filterRating) params.rating = filterRating;
      const res = await api.get('/reviews', { params });
      setReviews(res.data.reviews);
      setTotal(res.data.total);
    } catch { toast({ title: 'Erreur chargement avis', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [page, filterRating]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/reviews/stats');
      setStats(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchStats();
  }, [navigate, fetchStats]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await api.delete(`/reviews/${reviewId}`);
      toast({ title: 'Avis supprime' });
      fetchReviews();
      fetchStats();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' });
  };

  const maxDist = Math.max(...Object.values(stats.distribution), 1);

  if (loading) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      {/* Header */}
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold" data-testid="reviews-title">Avis Clients</h1>
            <p className="text-xs text-gray-300 mt-0.5">{stats.total} avis au total</p>
          </div>
          <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
            <ArrowLeft size={14} className="mr-1" /> Retour
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Average */}
          <div className="bg-white rounded-xl shadow-md p-5 text-center" data-testid="avg-rating-card">
            <p className="text-sm text-stone-500 mb-1">Note moyenne</p>
            <p className="text-4xl font-bold text-[#2c1810]">{stats.average || '-'}</p>
            <div className="flex justify-center gap-0.5 mt-2">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} size={18} className={s <= Math.round(stats.average) ? 'text-amber-400 fill-amber-400' : 'text-stone-300'} />
              ))}
            </div>
            <p className="text-xs text-stone-400 mt-1">{stats.total} avis</p>
          </div>

          {/* Distribution */}
          <div className="bg-white rounded-xl shadow-md p-5 md:col-span-2" data-testid="rating-distribution">
            <p className="text-sm text-stone-500 mb-3">Repartition des notes</p>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(r => (
                <div key={r} className="flex items-center gap-2">
                  <button
                    onClick={() => { setFilterRating(filterRating === r ? null : r); setPage(0); }}
                    className={`flex items-center gap-1 w-12 text-xs font-semibold cursor-pointer hover:underline ${filterRating === r ? 'text-amber-600' : 'text-stone-600'}`}
                    data-testid={`filter-${r}-star`}
                  >
                    {r} <Star size={11} className="text-amber-400 fill-amber-400" />
                  </button>
                  <div className="flex-1 bg-stone-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(stats.distribution[r] / maxDist) * 100}%`,
                        background: r >= 4 ? '#10b981' : r === 3 ? '#f59e0b' : '#ef4444',
                      }}
                    />
                  </div>
                  <span className="text-xs text-stone-500 w-8 text-right">{stats.distribution[r]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filter badge */}
        {filterRating && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold">
              Filtre : {filterRating} etoile{filterRating > 1 ? 's' : ''}
            </span>
            <button onClick={() => { setFilterRating(null); setPage(0); }} className="text-xs text-red-500 hover:underline">Effacer</button>
          </div>
        )}

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center text-stone-400" data-testid="no-reviews">
            {filterRating ? `Aucun avis avec ${filterRating} etoile${filterRating > 1 ? 's' : ''}` : 'Aucun avis pour le moment'}
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map(r => (
              <div key={r.review_id} className="bg-white rounded-xl shadow-sm border border-stone-100 p-4 flex gap-4 group" data-testid={`review-${r.review_id}`}>
                {/* Rating badge */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center text-white font-bold text-lg ${
                  r.rating >= 4 ? 'bg-emerald-500' : r.rating === 3 ? 'bg-amber-500' : 'bg-red-500'
                }`}>
                  {r.rating}
                  <Star size={10} className="fill-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-stone-800">{r.name || 'Anonyme'}</span>
                    {r.table_number && <span className="text-[10px] bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">Table {r.table_number}</span>}
                    <span className="text-[10px] text-stone-400 ml-auto">{formatDate(r.created_at)}</span>
                  </div>
                  <div className="flex gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={12} className={s <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'} />
                    ))}
                  </div>
                  {r.comment && <p className="text-sm text-stone-600 leading-relaxed">{r.comment}</p>}
                </div>

                {/* Delete */}
                <button
                  onClick={() => deleteReview(r.review_id)}
                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 self-start mt-1"
                  data-testid={`delete-review-${r.review_id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > PER_PAGE && (
          <div className="flex justify-center items-center gap-4 mt-6">
            <Button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} variant="outline" size="sm" className="text-xs">
              <ChevronLeft size={14} /> Precedent
            </Button>
            <span className="text-xs text-stone-500">Page {page + 1} / {Math.ceil(total / PER_PAGE)}</span>
            <Button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PER_PAGE >= total} variant="outline" size="sm" className="text-xs">
              Suivant <ChevronRight size={14} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsDashboard;
